import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import { Icon } from "@components/Icon";
import { FilterContext } from "~/providers/FilterProvider";
import { FiltersSelected, FiltersSelectedProps } from "@usereactify/search";
import React, { useContext } from "react";
import tw from "twin.macro";
import { FilterSectionClearAll } from "./FilterSectionClearAll";

const SelectedFilterButton = withFocusRing(tw.button`
  flex relative text-center border text-hint leading-input px-micro py-micro cursor-pointer
`);
const RemoveIcon = tw(Icon)`
  block my-auto pl-1
`;
const SelectedFilter = tw.span`
  block capitalize
`;
const FilterContainer = tw.div`
  flex flex-col block lg:hidden before:pointer-events-none before:z-10 before:absolute before:right-zero before:block before:w-mega before:h-mega before:bg-[linear-gradient(to right, rgba(243,241,237,0) 0%,rgba(243,241,237,1) 100%)]
`;
const ScrollWrapper = tw.ul`
  w-full px-midi list-none flex overflow-y-hidden overflow-x-auto space-x-1
`;
const Wrapper = tw.div`
  flex flex-wrap place-items-end list-none overflow-y-hidden overflow-x-hidden gap-1
`;

const Item = tw.li`
  flex-shrink-0 justify-center gap-1
`;
const ScrollActionsWrapper = tw.div`
  items-center justify-start px-midi
`;
const ActionsWrapper = tw.div`
  items-center justify-between flex flex-row
`;
const ClearAllWrapper = tw.div`
  mb-1-8 items-center my-1
`;

const SelectedFiltersContainer = tw.div`
  flex flex-col w-full
`;

const FilterNotice = tw.span`
  text-left block font-medium lg:hidden py-mini
`;

export type RenderFiltersSelectedProps = React.ComponentProps<
  NonNullable<FiltersSelectedProps["render"]>
>;

type FilterSectionSelectedFiltersProps = {
  layout?: "scroll" | "wrap";
};

type FiltersSelectedItemProps = {
  selectedFilter: RenderFiltersSelectedProps["selectedFilters"][number];
  handleRemove: RenderFiltersSelectedProps["handleRemove"];
};

type ValueWithLabel = { label: string };
type ValueWithLabelArray = ValueWithLabel[];
type HandleRemove = (key: string, value: string[]) => void;

type SingleRangeViewType = {
  selectedKey: string;
  value: ValueWithLabel;
  handleRemove: HandleRemove;
};
type SingleNOTRangeViewType = {
  selectedKey: string;
  value: string;
  handleRemove: HandleRemove;
};
type MultiRangeViewType = {
  selectedKey: string;
  value: ValueWithLabel;
  handleRemove: HandleRemove;
};
type MultiNOTRangeViewType = {
  selectedKey: string;
  value: string;
  handleRemove: HandleRemove;
};
type SliderViewType = { displaySliderPrefix: string; value: string[] };

const SingleRangeView = ({
  selectedKey,
  value,
  handleRemove,
}: SingleRangeViewType) => (
  <Item key={selectedKey} data-testid={selectedKey}>
    <SelectedFilterButton
      role="SelectedFilterButton"
      onClick={() => handleRemove(selectedKey, [value.label])}
    >
      <SelectedFilter>{value.label}</SelectedFilter>
      <RemoveIcon name="close" size="xxs" />
    </SelectedFilterButton>
  </Item>
);

const SingleNOTRangeView = ({
  selectedKey,
  value,
  handleRemove,
}: SingleNOTRangeViewType) => (
  <Item key={selectedKey} data-testid={selectedKey}>
    <SelectedFilterButton
      role="SelectedFilterButton"
      onClick={() => handleRemove(selectedKey, [value])}
    >
      <SelectedFilter>{value}</SelectedFilter>
      <RemoveIcon name="close" size="xxs" />
    </SelectedFilterButton>
  </Item>
);

const MultiRangeView = ({
  selectedKey,
  value,
  handleRemove,
}: MultiRangeViewType) => (
  <Item
    key={selectedKey + value?.label}
    data-testid={selectedKey + "-" + value?.label}
  >
    <SelectedFilterButton
      role="SelectedFilterButton"
      onClick={() => handleRemove(selectedKey, [value.label])}
    >
      <SelectedFilter>{value.label}</SelectedFilter>
      <RemoveIcon name="close" size="xxs" />
    </SelectedFilterButton>
  </Item>
);

const MultiNOTRangeView = ({
  selectedKey,
  value,
  handleRemove,
}: MultiNOTRangeViewType) => (
  <Item key={selectedKey + value} data-testid={selectedKey + "-" + value}>
    <SelectedFilterButton
      role="SelectedFilterButton"
      onClick={() => handleRemove(selectedKey, [value])}
    >
      <SelectedFilter>{value}</SelectedFilter>
      <RemoveIcon name="close" size="xxs" />
    </SelectedFilterButton>
  </Item>
);

const SliderView = ({ displaySliderPrefix, value }: SliderViewType) => (
  <Item key={"key-" + displaySliderPrefix} data-testid={displaySliderPrefix}>
    <SelectedFilterButton role="SelectedFilterButton">
      <SelectedFilter>{`${displaySliderPrefix}${value?.[0]} to ${displaySliderPrefix}${value?.[1]}`}</SelectedFilter>
      <RemoveIcon name="close" size="xxs" />
    </SelectedFilterButton>
  </Item>
);

const FiltersSelectedItem: React.FC<FiltersSelectedItemProps> = (props) => {
  const { filters } = useContext(FilterContext);
  const filter = filters?.find(
    (filter) => filter.handle === props.selectedFilter.key,
  );
  const { key, value } = props?.selectedFilter || {};
  const { displayType, displayView, displaySliderPrefix } = filter || {};

  return (
    <>
      {displayType === "single" && displayView === "range" && (
        <SingleRangeView
          selectedKey={key}
          value={value as ValueWithLabel}
          handleRemove={props.handleRemove}
        />
      )}
      {displayType === "single" && displayView !== "range" && (
        <SingleNOTRangeView
          selectedKey={key}
          value={value as string}
          handleRemove={props.handleRemove}
        />
      )}
      {displayType === "multi" &&
        displayView === "range" &&
        (value as ValueWithLabelArray)?.map((val) => (
          <MultiRangeView
            key={key}
            selectedKey={key}
            value={val}
            handleRemove={props.handleRemove}
          />
        ))}
      {displayType === "multi" &&
        displayView !== "range" &&
        (value as string[])?.map((val) => (
          <MultiNOTRangeView
            key={key}
            selectedKey={key}
            value={val}
            handleRemove={props.handleRemove}
          />
        ))}
      {displayType === "slider" && (
        <SliderView
          displaySliderPrefix={displaySliderPrefix as string}
          value={value as string[]}
        />
      )}
    </>
  );
};

const RenderFiltersSelectedScroll: React.FC<RenderFiltersSelectedProps> = (
  props,
) => {
  return (
    <FilterContainer>
      <ScrollWrapper
        className="hide-scrollbar"
        data-testid="selected-scroll-wrapper"
      >
        {props.selectedFilters?.map((selectedFilter) => (
          <FiltersSelectedItem
            key={selectedFilter?.key}
            selectedFilter={selectedFilter}
            handleRemove={props.handleRemove}
          />
        ))}
      </ScrollWrapper>
      <ScrollActionsWrapper>
        <ClearAllWrapper data-testid="clear-all-scroll">
          <FilterSectionClearAll />
        </ClearAllWrapper>
      </ScrollActionsWrapper>
    </FilterContainer>
  );
};

const RenderFiltersSelected: React.FC<RenderFiltersSelectedProps> = (props) => {
  return (
    <SelectedFiltersContainer>
      <FilterNotice>Filters Applied:</FilterNotice>
      <Wrapper data-testid="selected-default-wrapper">
        {props.selectedFilters?.map((selectedFilter) => (
          <FiltersSelectedItem
            key={selectedFilter?.key}
            selectedFilter={selectedFilter}
            handleRemove={props.handleRemove}
          />
        ))}
      </Wrapper>
      <ActionsWrapper>
        <ClearAllWrapper data-testid="clear-all">
          <FilterSectionClearAll />
        </ClearAllWrapper>
      </ActionsWrapper>
    </SelectedFiltersContainer>
  );
};

export const FilterSectionSelectedFilters = (
  props: FilterSectionSelectedFiltersProps,
) => {
  return (
    <FiltersSelected
      render={
        props.layout === "scroll"
          ? RenderFiltersSelectedScroll
          : RenderFiltersSelected
      }
    />
  );
};
